<template>
  <div>
    <v-dialog
      :model-value="show"
      width="550"
      @update:model-value="close"
    >
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title v-if="create">
              {{ $t('buttons.add-user.cta') }}
            </v-toolbar-title>
            <v-toolbar-title v-else>
              {{ $t('settings.user.upsert-user-dialog.label') }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <!-- TODO: check unsupported prop: dark -->
              <v-btn
                icon
                @click="close"
              >
                <v-icon size="large"> mdi-close </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="mt-6 pt-2 text-body-1 user-form">
          <div>
            <v-form
              ref="form"
              v-model="valid"
              name="update-user"
              @submit.prevent="editUser"
            >
              <v-text-field
                v-model="firstname"
                :label="$t('labels.first-name')"
                variant="outlined"
                :rules="[rules.required]"
                @click="onClick('First Name', firstname)"
              />
              <v-text-field
                v-model="lastname"
                :label="$t('labels.last-name')"
                variant="outlined"
                :rules="[rules.required]"
                @click="onClick('Last Name', lastname)"
              />
              <v-text-field
                v-model="email"
                :rules="[rules.email, rules.required]"
                variant="outlined"
                :label="$t('labels.email')"
                required
                @click="onClick('Email', email)"
              />
              <PhoneField
                v-model="phone"
                :loading="isLoading.phone"
                update-on-input
                :hint="$t('settings.user.upsert-user-dialog.mobile-hint')"
              />
              <Feature :feature-slug="featureNames.AUTHORIZATION">
                <template v-slot:default>
                  <div class="pt-6">
                    <h3>{{ $t('settings.user.upsert-user-dialog.title') }}</h3>
                    <p class="text-caption">
                      {{ $t('settings.user.upsert-user-dialog.subtitle') }}
                    </p>
                    <v-radio-group
                      v-model="role"
                      hide-details
                      :rules="[rules.required]"
                      class="role-select"
                    >
                      <v-radio
                        v-for="{ id: roleId, name, description } in roles"
                        :key="roleId"
                        :value="roleId"
                        @click="onClick('Role', name)"
                      >
                        <template v-slot:label>
                          <div class="ml-2">
                            <h4>{{ name }}</h4>
                            <p class="text-caption mb-0">
                              {{ description }}
                            </p>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </div>
                </template>
                <template v-slot:inactive>
                  <div class="pt-6">
                    <h3>{{ $t('settings.user.upsert-user-dialog.title') }}</h3>
                    <p class="text-caption">
                      {{ $t('settings.user.upsert-user-dialog.subtitle') }}
                    </p>
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <div v-bind="props">
                          <v-radio-group
                            hide-details
                            class="role-select"
                            disabled
                          >
                            <v-radio
                              v-for="{ id: roleId, name, description } in roles"
                              :key="roleId"
                              :value="roleId"
                              @click="onClick('Role', name)"
                            >
                              <template v-slot:label>
                                <div class="ml-2">
                                  <h4>{{ name }}</h4>
                                  <p class="text-caption mb-0">
                                    {{ description }}
                                  </p>
                                </div>
                              </template>
                            </v-radio>
                          </v-radio-group>
                        </div>
                      </template>
                      <span>{{ $t('settings.user.upsert-user-dialog.activation-hint') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </Feature>
            </v-form>
          </div>
        </v-card-text>

        <v-card-actions class="pb-4">
          <v-btn
            variant="text"
            @click="close"
          >
            {{ $t('labels.cancel') }}
          </v-btn>
          <v-spacer />

          <v-btn
            color="primary"
            class="px-4 mr-2 mb-2"
            :disabled="isLoading"
            :loading="isLoading"
            @click="submit"
          >
            {{ create ? $t('settings.user.upsert-user-dialog.create-account') : $t('labels.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PhoneField from '@/components/PhoneField.vue'
import eventBus, { eventNames } from '@/lib/eventBus'
import { email, required } from '@/lib/validation'
import featureMixin from '@/mixins/feature'

import CREATE_USER from './queries/CreateUser.gql'
import ROLES from './queries/Roles.gql'
import UPDATE_USER from './queries/UpdateUser.gql'
import USERS from './queries/Users.gql'

export default {
  components: { PhoneField },
  mixins: [featureMixin],
  props: {
    user: { type: Object, default: () => ({}) },
    create: { type: Boolean, default: false },
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      rules: { email, required },
      isLoading: false,
      valid: false,
      defaultRole: null,
      id: this.user?.id,
      firstname: this.user?.firstname,
      lastname: this.user?.lastname,
      email: this.user?.email,
      role: this.user?.role?.id || this.defaultRole,
      phone: this.user?.phone,
    }
  },
  watch: {
    user: {
      handler(user) {
        this.id = user?.id
        this.firstname = user?.firstname
        this.lastname = user?.lastname
        this.email = user?.email
        this.role = user?.role?.id || this.defaultRole
        this.phone = user?.phone
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      },
      deep: true,
    },
  },
  methods: {
    onClick(label, value) {
      this.$tracking.event('Settings', 'Clicked', label, value)
    },
    async close() {
      this.$emit('close')
    },
    async submit() {
      const { valid } = await this.$refs.form.validate()
      if (!valid) {
        return
      }
      if (this.create) {
        await this.createUser()
      } else {
        await this.editUser()
      }
    },
    async editUser() {
      try {
        this.isLoading = true
        await this.$apollo.mutate({
          mutation: UPDATE_USER,
          variables: {
            input: {
              id: this.user.id,
              firstname: this.firstname,
              lastname: this.lastname,
              email: this.email,
              phone: this.phone,
              ...(this.isFeatureActive(this.featureNames.AUTHORIZATION) && { role: this.role }),
            },
          },
          refetchQueries: [
            {
              query: USERS,
              variables: { input: { companyId: this.$auth.user.companyId } },
            },
          ],
        })
        this.$tracking.event('Settings', 'Clicked', 'Edit User')
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.user.success'),
        })
        this.close()
      } catch (e) {
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.user.error'),
        })
      } finally {
        this.isLoading = false
      }
    },
    async createUser() {
      try {
        this.isLoading = true
        await this.$apollo.mutate({
          mutation: CREATE_USER,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              firstname: this.firstname,
              lastname: this.lastname,
              email: this.email,
              phone: this.phone,
              ...(this.isFeatureActive(this.featureNames.AUTHORIZATION) && { role: this.role }),
              receiveNotificationEmails: {
                leadAction: true,
              },
            },
          },
          refetchQueries: [
            {
              query: USERS,
              variables: { input: { companyId: this.$auth.user.companyId } },
            },
          ],
        })
        this.$tracking.event('Settings', 'Clicked', 'Create User')
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.user.upsert-texts.0'),
          texts: [this.$t('alerts.settings.user.upsert-texts.1')],
          time: 10000,
        })
        this.close()
        this.firstname = null
        this.lastname = null
        this.email = null
        this.role = null
        this.phone = null
      } catch (e) {
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.user.error'),
        })
      } finally {
        this.isLoading = false
      }
    },
    translateRoleDescription(slug) {
      return {
        administrator: this.$t('settings.user.upsert-user-dialog.roles-description.administrator'),
        manager: this.$t('settings.user.upsert-user-dialog.roles-description.manager'),
        makler: this.$t('settings.user.upsert-user-dialog.roles-description.makler'),
        'account-owner': this.$t(
          'settings.user.upsert-user-dialog.roles-description.account-owner'
        ),
        'test-no-permissions': this.$t(
          'settings.user.upsert-user-dialog.roles-description.test-no-permissions'
        ),
        'marketing-expert': this.$t(
          'settings.user.upsert-user-dialog.roles-description.marketing-expert'
        ),
      }[slug]
    },
  },
  apollo: {
    roles: {
      query: ROLES,
      fetchPolicy: 'network-only',
      skip() {
        return !this.show
      },
      update({ roles }) {
        roles = roles.map((role) => {
          return {
            ...role,
            description: this.translateRoleDescription(role.slug) || role.description,
          }
        })

        this.defaultRole = this.role || roles.find((role) => role.isDefault)?.id
        return roles.filter((role) => role.isAssignable)
      },
    },
  },
  emits: ['close'],
}
</script>

<style scoped>
.user-form {
  max-height: 600px;
  overflow: auto;
}

.role-select .v-item--active {
  background-color: rgb(230, 230, 230);
}
.role-select .v-radio {
  border-radius: 8px;
  padding: 8px;
}
</style>
